import { useQuery } from '@vue/apollo-composable';
import { defineStore } from 'pinia';
import { IContentStoreState } from '../../interfaces/IContentStoreState';
import * as Queries from '../../queries';
import * as ApiConstant from '../../apiConstant';
import { IModalDatasource, ISearchOptionDatasource, ModalType } from '../../interfaces/datasources/IDatasourceProps';
import { DefaultCarDetailsDatasourceId, DefaultCompareVehiclesDatasourceId } from '../../apiConstant';

export const useContentStore = defineStore('contentStore', {
  state: (): IContentStoreState => ({
    lastUpdated: '',
    searchWidgetProps: {
      populated: false,
      moduleTitle: '',
      defaultSearchOption: '',
      searchOptions: [],
      searchButtonText: '',
    },
    shareCopy: {
      populated: false,
      copyLinkText: '',
      shareLinkText: '',
      emailBody: '',
      emailSubject: '',
    },
    comparisonFooter: {
      populated: false,
      viewComparisonButtonText: '',
      comparisonTextPrefix: '',
      comparisonTextSuffix: '',
      closeButtonText: '',
      carTilePlaceholderText: '',
      carLoadingText: '',
    },
    makeModelModalProps: {
      populated: false,
      searchPlaceholder: '',
      searchOptionHeading: '',
      searchButtonText: '',
      clearOptionText: '',
    },
    searchResultsProps: {
      moduleTitle: '',
      numToFetch: 0, // Set this to zero in order to ensure loading awaits the result from content API
      showMoreText: '',
      noMoreResultsText: '',
      noResultsFoundHeadingText: '',
      noResultsFoundDescriptionText: '',
      noResultsFoundStartNewSearchText: '',
      noResultsFoundRefineSearchText: '',
      noResultsFoundStartNewSearchButtonText: '',
      populated: false,
    },
    carDetailsProps: {
      // Not found messages
      noVehicleFoundHeadingText: '',
      noVehicleFoundDescriptionText: '',
      noVehicleFoundStartNewSearchText: '',
      noVehicleFoundStartNewSearchClosingText: '',
      noVehicleFoundStartNewSearchButtonText: '',
      // Car Details Key Stats labels
      configuration: '',
      fuel: '',
      emissionsRating: '',
      costToFuelRecharge: '',
      // Cost to run labels
      costToFillLabel: '',
      costPer100kmLabel: '',
      registrationLabel: '',
      // Cost to run links (links are in JSS format)
      batteriesLabel: '',
      batteriesLink: undefined,
      insuranceLabel: '',
      insuranceLink: undefined,
      roadsideLabel: '',
      roadsideLink: undefined,
      populated: false,
    },
    fuelPrices: {
      fuelPriceData: [],
      populated: false,
    },
    registrationCosts: {
      registrationCosts: [],
      populated: false,
    },
    carDetailsTabularDataProps: { tabularData: [], populated: false },
    compareVehiclesTabularDataProps: { tabularData: [], populated: false },
    modalDatasources: { populated: false, modalContentItems: {} as Record<ModalType, IModalDatasource> },
  }),
  actions: {
    /**refresh the content - by setting populated to false
     * if you add new content above make sure the populated is set to false for that in this method for content to be refreshed
     */
    refreshContent() {
      this.searchResultsProps.populated = false;
      this.makeModelModalProps.populated = false;
      this.comparisonFooter.populated = false;
      this.shareCopy.populated = false;
      this.searchWidgetProps.populated = false;
      this.fuelPrices.populated = false;
      this.registrationCosts.populated = false;
      this.carDetailsProps.populated = false;
      this.carDetailsTabularDataProps.populated = false;
      this.compareVehiclesTabularDataProps.populated = false;
      this.modalDatasources.populated = false;
    },
    async fetchSearchWidgetProps() {
      if (!this.searchWidgetProps.populated) {
        const queryVars = { datasource: ApiConstant.DefaultSearchWidgetDatasourceId };
        useQuery(Queries.SEARCH_WIDGET_DS_QUERY, queryVars).onResult((result) => {
          this.searchWidgetProps.moduleTitle = result.data.datasource.moduleTitle?.value;
          this.searchWidgetProps.searchButtonText = result.data.datasource.searchButtonText?.value;
          this.searchWidgetProps.defaultSearchOption = result.data.datasource.defaultSearchType?.targetItem?.listValue.value;
          this.searchWidgetProps.searchOptions = result.data.datasource.searchTypes.targetItems.map((searchOption: ISearchOptionDatasource) => {
            return {
              label: searchOption.listLabel.value,
              value: searchOption.listValue.value,
              default: this.searchWidgetProps.defaultSearchOption === searchOption.listValue.value,
            };
          });
          this.searchWidgetProps.populated = true;
        });
      }
    },
    async fetchShareCopy() {
      if (!this.shareCopy.populated) {
        const queryVars = { datasource: ApiConstant.ShareCopyDatasourceId };
        useQuery(Queries.SHARE_COPY_DS_QUERY, queryVars).onResult((result) => {
          this.shareCopy.copyLinkText = result.data.datasource.copyLinkText?.value;
          this.shareCopy.shareLinkText = result.data.datasource.shareLinkText?.value;
          this.shareCopy.emailSubject = result.data.datasource.emailSubject?.value;
          this.shareCopy.emailBody = result.data.datasource.emailBody?.value;
          this.shareCopy.populated = true;
        });
      }
    },
    async fetchComparisonFooter() {
      if (!this.comparisonFooter.populated) {
        const queryVars = { datasource: ApiConstant.ComparisonFooterDatasourceId };
        useQuery(Queries.COMPARISON_FOOTER_DS_QUERY, queryVars).onResult((result) => {
          this.comparisonFooter.viewComparisonButtonText = result.data.datasource.viewComparisonButtonText?.value;
          this.comparisonFooter.comparisonTextPrefix = result.data.datasource.comparisonTextPrefix?.value;
          this.comparisonFooter.comparisonTextSuffix = result.data.datasource.comparisonTextSuffix?.value;
          this.comparisonFooter.closeButtonText = result.data.datasource.closeButtonText?.value;
          this.comparisonFooter.carTilePlaceholderText = result.data.datasource.carTilePlaceholderText?.value;
          this.comparisonFooter.carLoadingText = result.data.datasource.carLoadingText?.value;
          this.comparisonFooter.populated = true;
        });
      }
    },
    async fetchMakeModelModal() {
      if (!this.makeModelModalProps.populated) {
        const queryVars = { datasource: ApiConstant.MakeModelModalDatasourceId };
        useQuery(Queries.MAKE_MODEL_MODAL_DS_QUERY, queryVars).onResult((result) => {
          this.makeModelModalProps.searchPlaceholder = result.data.datasource.searchPlaceholder?.value;
          this.makeModelModalProps.searchOptionHeading = result.data.datasource.searchOptionHeading?.value;
          this.makeModelModalProps.searchButtonText = result.data.datasource.searchButtonText?.value;
          this.makeModelModalProps.clearOptionText = result.data.datasource.clearOptionText?.value;
          this.makeModelModalProps.populated = true;
        });
      }
    },
    async fetchSearchResultsProps(dsId?: string) {
      if (!this.searchResultsProps.populated) {
        const queryVars = { datasource: dsId };
        useQuery(Queries.SEARCH_RESULTS_DS_QUERY, queryVars).onResult((result) => {
          this.searchResultsProps.moduleTitle = result.data.datasource.moduleTitle?.value;
          this.searchResultsProps.numToFetch = Number(result.data.datasource.numberOfTilesToFetch?.value);
          this.searchResultsProps.showMoreText = result.data.datasource.showMoreText?.value;
          this.searchResultsProps.noMoreResultsText = result.data.datasource.noMoreResultsText?.value;
          this.searchResultsProps.noResultsFoundHeadingText = result.data.datasource.noResultsFoundHeadingText?.value;
          this.searchResultsProps.noResultsFoundDescriptionText = result.data.datasource.noResultsFoundDescriptionText?.value;
          this.searchResultsProps.noResultsFoundStartNewSearchText = result.data.datasource.noResultsFoundStartNewSearchText?.value;
          this.searchResultsProps.noResultsFoundRefineSearchText = result.data.datasource.noResultsFoundRefineSearchText?.value;
          this.searchResultsProps.noResultsFoundStartNewSearchButtonText = result.data.datasource.noResultsFoundStartNewSearchButtonText?.value;
          this.searchResultsProps.populated = true;
        });
      }
    },
    async fetchCarDetailsProps(dsId?: string) {
      if (!this.carDetailsProps.populated) {
        const queryVars = { datasource: dsId || DefaultCarDetailsDatasourceId };
        useQuery(Queries.CAR_DETAILS_DS_QUERY, queryVars).onResult((result) => {
          this.carDetailsProps.noVehicleFoundHeadingText = result.data.datasource?.noVehicleFoundHeadingText?.value;
          this.carDetailsProps.noVehicleFoundDescriptionText = result.data.datasource?.noVehicleFoundDescriptionText?.value;
          this.carDetailsProps.noVehicleFoundStartNewSearchText = result.data.datasource?.noVehicleFoundStartNewSearchText?.value;
          this.carDetailsProps.noVehicleFoundStartNewSearchClosingText = result.data.datasource?.noVehicleFoundStartNewSearchClosingText?.value;
          this.carDetailsProps.noVehicleFoundStartNewSearchButtonText = result.data.datasource?.noVehicleFoundStartNewSearchButtonText?.value;
          this.carDetailsProps.configuration = result.data.datasource?.configuration?.value;
          this.carDetailsProps.fuel = result.data.datasource?.fuel?.value;
          this.carDetailsProps.emissionsRating = result.data.datasource?.emissionsRating?.value;
          this.carDetailsProps.costToFuelRecharge = result.data.datasource?.costToFuelRecharge?.value;
          this.carDetailsProps.costToFillLabel = result.data.datasource?.costToFillLabel?.value;
          this.carDetailsProps.costPer100kmLabel = result.data.datasource?.costPer100kmLabel?.value;
          this.carDetailsProps.registrationLabel = result.data.datasource?.registrationLabel?.value;
          this.carDetailsProps.batteriesLabel = result.data.datasource?.batteriesLabel?.value;
          this.carDetailsProps.batteriesLink = result.data.datasource?.batteriesLink?.jss?.value;
          this.carDetailsProps.insuranceLabel = result.data.datasource?.insuranceLabel?.value;
          this.carDetailsProps.insuranceLink = result.data.datasource?.insuranceLink?.jss?.value;
          this.carDetailsProps.roadsideLabel = result.data.datasource?.roadsideLabel?.value;
          this.carDetailsProps.roadsideLink = result.data.datasource?.roadsideLink?.jss?.value;
          this.carDetailsProps.populated = true;
        });
      }
    },
    async fetchCarDetailsTabularDataProps(dsId?: string) {
      if (!this.carDetailsTabularDataProps.populated) {
        const queryVars = { datasource: dsId || DefaultCarDetailsDatasourceId };
        useQuery(Queries.TABULAR_DATA_DS_QUERY, queryVars).onResult((result) => {
          this.carDetailsTabularDataProps.tabularData = result.data.datasource?.tabularData.targetItem;
          this.carDetailsTabularDataProps.populated = true;
        });
      }
    },
    async fetchCompareVehiclesTabularDataProps(dsId?: string) {
      if (!this.compareVehiclesTabularDataProps.populated) {
        const queryVars = { datasource: dsId || DefaultCompareVehiclesDatasourceId };
        useQuery(Queries.TABULAR_DATA_DS_QUERY, queryVars).onResult((result) => {
          this.compareVehiclesTabularDataProps.tabularData = result.data.datasource?.tabularData.targetItem;
          this.compareVehiclesTabularDataProps.populated = true;
        });
      }
    },
    async fetchFuelPrices() {
      if (!this.fuelPrices.populated) {
        const queryVars = { datasource: ApiConstant.FuelPriceDatasourceId };
        useQuery(Queries.FUEL_PRICE_DS_QUERY, queryVars).onResult((result) => {
          this.fuelPrices.fuelPriceData = result.data.datasource.children.map((item: any) => {
            return {
              fuelPrice: item.fuelPrice.value as string,
              fuelType: item.fuelType.targetItem.product.value as string,
            };
          });
          this.fuelPrices.populated = true;
        });
      }
    },
    async fetchRegistrationCosts() {
      if (!this.registrationCosts.populated) {
        const queryVars = { datasource: ApiConstant.RegistrationCostsDatasourceId };
        useQuery(Queries.REGISTRATION_COSTS_DS_QUERY, queryVars).onResult((result) => {
          this.registrationCosts.registrationCosts = result.data.datasource.children.map((item: any) => {
            return {
              cylinders: item.cylinders.value as string,
              registrationCost: item.registrationCost.value as string,
            };
          });
          this.registrationCosts.populated = true;
        });
      }
    },

    async fetchModalDatasources() {
      if (!this.modalDatasources.populated) {
        useQuery(Queries.ALL_MODALS_DS_QUERY).onResult((result) => {
          const data: any = {};
          this.modalDatasources.modalContentItems = result.data.datasource.children.forEach((item: IModalDatasource) => {
            data[item.key.value as ModalType] = { title: item.title, description: item.description, key: item.key };
          });
          this.modalDatasources.populated = true;
          this.modalDatasources.modalContentItems = data as Record<ModalType, IModalDatasource>;
        });
      }
    },
  },

  getters: {},
  persist: true,
});
